<script>
import moment from 'moment'
import ButtonGroup from '@kvass/button-group'
import { DropdownComponent } from 'vue-elder-dropdown'
import FromNow from '@/components/Moment/FromNow'
import Bus from '@/bus'
import graphql from '@/api/graphql'
import fetch from '@/fetch'
import { isTaskRelated } from '../utils'
import { SimpleTemplateEngine } from '@/utils'
import * as Filters from '@/filters'
import Referrers from '@/components/Lead/Referrers'
import Tags from '@/components/Tags'
import API from '@/api/Lead'
import { LeadPathByModel } from '@/components/Lead/utils'

export default {
  props: {
    item: Object,
    column: Object,
    properties: Array,
    variant: String,
    reference: String,
    onModel: String,
  },
  inject: ['columns', 'getSort', 'getItems'],
  computed: {
    items() {
      return this.getItems(this.column)
    },

    activity() {
      const activity = this.$path('activities.0', this.item)
      if (!activity) return

      if (activity.event === 'note') return { date: activity.date, message: activity.message }
      return { date: activity.date, message: this.$t(activity.event) }
    },
    context() {
      return LeadPathByModel[this.onModel]
    },
    scopes() {
      if (this.item.scopes.every(s => s.onModel === 'Project')) return
      return this.item.scopes
        .filter(s => s.onModel === 'Residential')
        .map(s => s.number)
        .join(', ')
    },
    customLines() {
      return this.$path('enums.KanbanSettings.customLines', this.$store.state)
    },
    actions() {
      const modalOptions = {
        lead: this.item.id,
        listeners: {
          'create:activity': activity => {
            this.item.activities = [activity, ...this.item.activities]
          },
          'processed:comment': (comment, all) => {
            this.item.stats.unprocessedComments = all ? 0 : this.item.stats.unprocessedComments - 1
          },
        },
      }

      return [
        {
          condition: this.item.contact.phone,
          label: this.$t('call'),
          icon: 'fa-pro-light:phone',
          listeners: {
            click: () =>
              Bus.emit('lead-modal:show', {
                ...modalOptions,
                intent: 'call',
              }),
          },
        },
        {
          label: this.$t('sendEmail'),
          icon: 'fa-pro-light:envelope',
          'data-counter': this.item.stats.unprocessedComments,
          'data-counter-urgent': true,
          listeners: {
            click: () =>
              Bus.emit('lead-modal:show', {
                ...modalOptions,
                intent: 'message',
              }),
          },
        },
        {
          label: this.$tc('activity', 2),
          icon: 'fa-pro-light:timeline-arrow',
          listeners: {
            click: () =>
              Bus.emit('lead-modal:show', {
                ...modalOptions,
                intent: 'activity',
              }),
          },
        },
        {
          label: `${this.$tc('task', 2)} (${this.item.stats.urgentTasks} ${this.$t('urgentTasksLabel')})`,
          icon: 'fa-pro-light:clipboard-check',
          'data-counter': this.item.stats.openTasks,
          'data-counter-urgent': Boolean(this.item.stats.urgentTasks),
          listeners: {
            click: () =>
              Bus.emit('lead-modal:show', {
                ...modalOptions,
                intent: 'tasks',
              }),
          },
        },
      ]
        .filter(a => !('condition' in a) || Boolean(a.condition))
        .map(({ condition, ...rest }) => ({
          ...rest,
          iconPlacement: 'left',
        }))
    },
    isFirst() {
      return this.items[0] === this.item
    },
    isLast() {
      return this.items[this.items.length - 1] === this.item
    },
  },
  methods: {
    getCustomLineContent(line) {
      if (/[{}]/g.test(line.value))
        return SimpleTemplateEngine(line.value, this.item, { strict: true, filters: Filters }) || line.default
      return this.$path(line.value, this.item) || line.default
    },
    createTask(close) {
      this.$bus.$emit('task', { reference: { onModel: 'Lead', ref: this.item.id } })
      if (close) close()
    },
    onStatusChange(col, close) {
      this.$emit('change', {
        target: this.item,
        value: col.id,
        sort: this.getSort({ column: col, item: this.item, index: 0 }),
      })
      close()
    },
    sendToTop(close) {
      this.$emit('change', {
        target: this.item,
        sort: this.getSort({ column: this.column, item: this.item, index: 0 }),
      })
      close()
      this.$nextTick(() => this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }))
    },
    sendToBottom(close) {
      this.$emit('change', {
        target: this.item,
        sort: this.getSort({ column: this.column, item: this.item, index: -1 }),
      })
      close()
      this.$nextTick(() => this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }))
    },
    refreshStats() {
      return graphql
        .request(`query { Lead(id: "${this.item.id}") { stats { openTasks, urgentTasks } } }`)
        .then(data => {
          Object.entries(data.Lead.stats).forEach(([k, v]) => (this.item.stats[k] = v))
        })
    },
    onArchiveClick(close) {
      const { id, isArchived } = this.item
      fetch(`/api/lead/${id}/${isArchived ? 'unarchive' : 'archive'}`, { method: 'POST' })
        .then(() => (this.item.isArchived = !this.item.isArchived))
        .finally(() => close())
    },
    setTags(tags) {
      API.setTags(tags, this.item.id)
    },
  },
  created() {
    const onTaskEvent = async task => {
      if (!(await isTaskRelated(task, this.item.id))) return
      this.refreshStats()
    }
    this.$bus.on('task:update', onTaskEvent)
    this.$bus.on('task:create', onTaskEvent)
  },
  components: {
    ButtonGroup,
    DropdownComponent,
    FromNow,
    Referrers,
    Tags,
  },
}
</script>

<template>
  <div
    :class="[
      'kanban-item-lead',
      'kanban-item-lead--variant-' + variant,
      { 'kanban-item-lead--archived': item.isArchived },
    ]"
    :data-contact="item.contact.id"
    :data-lead="item.id"
  >
    <div class="kanban-item-lead__header">
      <div v-if="item.isArchived" class="kanban-item-lead__archived" theme="warning" icon="fa-pro-light:box-archive">
        {{ $t('archived') }}
      </div>
      <div>
        <b
          v-if="$path('contact.company.name', item)"
          data-part="company"
          v-tooltip="`${$t('orgNo')} ${item.contact.company.organizationNumber}`"
          >{{ item.contact.company.name }}</b
        >
        <b data-part="name">{{ item.contact.name }}</b>
        <div v-if="scopes" data-part="scopes"><Icon icon="fa-pro-solid:thumbtack" />&nbsp;&nbsp;{{ scopes }}</div>
      </div>
      <slot name="primary-actions">
        <ButtonGroup :use-portal="false">
          <ButtonComponent
            icon="fa-pro-light:user"
            tag="router-link"
            data-part="action"
            :to="{ name: `${context}-lead`, params: { id: item.id, reference } }"
            v-tooltip="{ content: $t('showLead'), delay: 150 }"
          />
          <template #secondary="{ close }">
            <ButtonComponent
              icon="fa-pro-light:user-edit"
              tag="router-link"
              :label="$t('editContact')"
              :to="{
                name: 'contact-edit',
                params: { id: item.contact.id },
                query: { returnTo: $route.fullPath, current: reference },
              }"
            />
            <ButtonComponent :label="$t('createTask')" icon="fa-pro-light:clipboard" @click="() => createTask(close)" />
            <template v-if="!(isFirst && isLast)">
              <strong>{{ $t('priority') }}</strong>
              <ButtonComponent
                v-if="!isFirst"
                :label="$t('sendToTop')"
                icon="fa-pro-light:arrow-up-to-line"
                @click="() => sendToTop(close)"
              />
              <ButtonComponent
                v-if="!isLast"
                :label="$t('sendToBottom')"
                icon="fa-pro-light:arrow-down-to-line"
                @click="() => sendToBottom(close)"
              />
            </template>
            <strong>{{ $t('changeStatus') }}</strong>
            <ButtonComponent
              v-for="col in columns"
              :label="col.label"
              icon="fa-pro-solid:circle"
              data-part="status-change"
              :style="`--circle-color: ${col.color};`"
              @click="() => onStatusChange(col, close)"
            />
            <hr />
            <ButtonComponent
              :label="$t(!item.isArchived ? 'archive' : 'restore')"
              confirm
              :icon="`fa-pro-light:${item.isArchived ? 'undo' : 'box-archive'}`"
              @click="() => onArchiveClick(close)"
            />
          </template>
        </ButtonGroup>
      </slot>
    </div>
    <hr />
    <template v-if="activity && properties.includes('lastActivity')">
      <div class="kanban-item-lead__content">
        <div class="kanban-item-lead__property kanban-item-lead__property--full">
          <span>{{ $t('lastActivity') }}:</span>
          <FromNow :date="activity.date" />
        </div>
        <div class="kanban-item-lead__message">{{ activity.message }}</div>
      </div>
      <hr />
    </template>
    <div class="kanban-item-lead__footer">
      <ButtonComponent
        v-for="{ listeners, label, ...props } in actions"
        v-tooltip="{ content: label, delay: 150 }"
        v-bind="props"
        v-on="listeners"
        data-part="action"
      />
      <div class="kanban-item-lead__properties">
        <div
          v-if="properties.includes('firstActivity')"
          class="kanban-item-lead__property kanban-item-lead__property--full"
        >
          <span>{{ $t('firstActivity') }}:</span>
          <FromNow :date="item.createdAt" />
        </div>
        <div v-if="properties.includes('referrer')" class="kanban-item-lead__property kanban-item-lead__property--full">
          <span>{{ $tc('leadReferrer', 1) }}:</span>
          <Referrers :value="item.referrer">
            <template #fallback>-</template>
          </Referrers>
        </div>
        <div v-if="properties.includes('tags')" class="kanban-item-lead__property kanban-item-lead__property--full">
          <span>{{ $tc('tag', 2) }}:</span>
          <Tags
            v-model="item.tags"
            :ignored="$store.state.enums.SystemTags"
            @input="setTags"
            :items="`/api/${context}/${reference}/lead/tags`"
          />
        </div>
        <div
          v-for="l in customLines"
          :class="['kanban-item-lead__property', { 'kanban-item-lead__property--full': !l.size || l.size === 2 }]"
        >
          <span>{{ l.label }}:</span>
          <component :is="l.component || 'span'" v-bind="l.props || {}">{{ getCustomLineContent(l) }}</component>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.kanban-item-lead {
  --kanban-item-lead-padding: 0.75rem;
  --kanban-item-lead-inner-padding: 0.75rem;
  --kanban-item-lead-gap: 0.75rem;

  scroll-margin: 100px;

  background-color: var(--k-ui-color-neutral-lightest);
  border: 1px solid var(--k-ui-color-neutral);
  border-radius: var(--border-radius);

  padding: var(--kanban-item-lead-padding);

  display: flex;
  flex-direction: column;
  gap: var(--kanban-item-lead-gap);

  position: relative;

  box-shadow: 0 4px 12px -6px var(--k-ui-shadow-color);

  &--archived {
    box-shadow: none;
    border-style: dashed;
    @include Stripes(var(--k-ui-color-neutral-lightest), var(--k-ui-color-neutral-light), -55deg, 20px);
  }

  &--variant-select {
    .kanban-item-lead__content,
    .kanban-item-lead__footer,
    hr {
      display: none;
    }
  }

  &__header,
  &__content,
  &__footer {
    // padding: var(--kanban-item-lead-padding);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--kanban-item-lead-gap);
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--kanban-item-lead-gap) / 2);
  }

  [data-part='action'],
  .elder-dropdown__trigger > .elder-button {
    flex-grow: 1;
    font-size: 0.8em;
    justify-content: center;

    background-color: white;
    border-color: var(--k-ui-color-neutral);

    svg {
      font-size: 1.15em;
    }

    &[data-counter]:not([data-counter='0']) {
      &:after {
        display: block;
        content: attr(data-counter);
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(25%, -25%);
        z-index: 2;
        font-weight: bold;

        background-color: css-lighten('k-ui-color-warning-light', 20%);
        border: 1px solid css-darken('k-ui-color-warning-light', 10%);
        font-weight: bold;

        padding: 0.15em 0.5em;
        border-radius: 99px;
      }
    }

    &[data-counter-urgent='true'] {
      &:after {
        background-color: var(--k-ui-color-danger) !important;
        border-color: var(--k-ui-color-danger) !important;
        color: white;
      }
    }
  }

  [data-part='company'],
  [data-part='name'] {
    display: block;
  }

  [data-part='company'] {
    & + [data-part='name'] {
      font-weight: normal;
      font-size: 0.8em;
    }
  }

  &__properties {
    flex-grow: 1;
    margin-top: 0.5rem;

    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.5rem;

    .kanban-item-lead__property:first-child {
      margin-bottom: 0.25rem;
    }
  }

  &__property {
    font-size: 0.8em;

    display: flex;
    gap: 0.5em 1rem;
    justify-content: space-between;

    flex-grow: 1;

    &--full {
      flex-basis: 100%;
    }

    span:first-child {
      font-weight: bold;
    }
    span:last-child {
    }
  }

  [data-part='scopes'] {
    font-size: 0.8em;
    opacity: 0.8;
    margin-top: 2px;

    svg path {
      fill: var(--k-ui-color-neutral-dark);
    }
  }

  & > hr {
    width: 100%;
    margin: 0;
    border-color: var(--k-ui-color-neutral);
  }

  &__message {
    $lines: 2;
    display: -webkit-box;
    -webkit-line-clamp: $lines;
    line-clamp: $lines;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__archived {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, -50%) rotate(-45deg);
    letter-spacing: 0.5ch;
    opacity: 0.2;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 1.5em;
    z-index: 1;

    pointer-events: none;
  }

  [data-part='tags'] {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }
}
</style>
